import React, { useEffect, useRef } from 'react';

import { ModelCache } from 'app/scripts/db/model-cache';
import type { View } from 'app/scripts/views/internal/view';

type ViewOptions = void | {
  [key: string]: unknown;
};

interface ViewConstructable<
  TOptions extends ViewOptions,
  TModel,
  TView extends View<TOptions, TModel>
> {
  new (options: TOptions): TView;
}

export const BackboneViewAsComponent = <
  TOptions extends ViewOptions,
  TModel,
  TView extends View<TOptions, TModel>
>({
  View,
  onViewRendered,
  model,
  options,
  className,
}: {
  View: ViewConstructable<TOptions, TModel, TView>;
  onViewRendered?: (view: unknown) => void;
  model: TModel;
  options: TOptions;
  className?: string;
}) => {
  const thisRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!thisRef.current) {
      return;
    }

    const view = new View({ ...options, model, modelCache: ModelCache });

    view.render();

    thisRef.current.append(view.el);

    if (onViewRendered) {
      onViewRendered(view);
    }

    return () => {
      view.remove();
    };
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [thisRef]);

  return <div className={className} ref={thisRef} />;
};
